'use client';

import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';

interface SearchBarProps {
  value?: string;
  placeholder?: string;
  onSearch: (query: string) => void;
  debounceMs?: number;
  className?: string;
  showResults?: boolean;
  resultCount?: number;
  loading?: boolean;
}

export default function SearchBar({
  value = '',
  placeholder = 'Buscar productos...',
  onSearch,
  debounceMs = 300,
  className = '',
  showResults = false,
  resultCount,
  loading = false
}: SearchBarProps) {
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (inputValue !== value) {
        onSearch(inputValue);
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, onSearch, debounceMs, value]);

  const handleClear = () => {
    setInputValue('');
    onSearch('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      inputRef.current?.blur();
    }
  };

  // Global keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className={`relative transition-all duration-200 ${
        isFocused ? 'ring-2 ring-brand-primary/20 shadow-lg' : 'shadow-sm'
      } rounded-lg`}>
        <div className="relative">
          <Search 
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors duration-200 ${
              isFocused || inputValue ? 'text-brand-primary' : 'text-gray-400'
            } h-5 w-5`}
          />
          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            className={`w-full pl-10 pr-20 py-3 text-sm border border-gray-300 rounded-lg 
              focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent
              transition-all duration-200 bg-white
              ${isFocused ? 'bg-white' : 'bg-gray-50/50'}
              hover:bg-white hover:border-gray-400
            `}
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
            {!isFocused && !inputValue && (
              <div className="hidden sm:flex items-center space-x-1 px-2 py-1 bg-gray-100 rounded text-xs text-gray-500">
                <span>⌘</span>
                <span>K</span>
              </div>
            )}
            {loading && (
              <Loader2 className="h-4 w-4 text-brand-primary animate-spin" />
            )}
            {inputValue && !loading && (
              <button
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Limpiar búsqueda"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search Results Indicator - Only show when focused or loading */}
      {showResults && inputValue && (isFocused || loading) && (
        <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-white border border-gray-200 rounded-md shadow-md z-50">
          <div className="text-xs text-gray-600">
            {loading ? (
              <div className="flex items-center">
                <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                Buscando "{inputValue}"...
              </div>
            ) : resultCount !== undefined ? (
              resultCount > 0 ? (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></div>
                  <span>
                    <span className="font-medium text-brand-primary">{resultCount}</span>
                    {' '}resultado{resultCount !== 1 ? 's' : ''} encontrado{resultCount !== 1 ? 's' : ''}
                  </span>
                </div>
              ) : (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mr-1.5"></div>
                  No se encontraron resultados
                </div>
              )
            ) : (
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-1.5"></div>
                Preparando búsqueda...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}